import { base64DecodedLen, base64DecToArr } from "../encoding/base64";
import { blobToJson } from "../encoding/utf8";
import { ColumnFamilies, ElementFlags, QC, isFamilyType, streamFlag } from "./dt-schema";
import { dayjs } from "../streams/timeline/time";

export const AttributeType =
	{
		//Numeric types
		Unknown: 0,
		Boolean: 1,
		Integer: 2,
		Double: 3,
		Float: 4,

		//Special types
		BLOB: 10,
		DbKey: 11, /* represents a link to another object in the database, using database internal ID */
		DbKeyList: 12, /* represents a list of links to other objects within the same model */
		ExDbKeyList: 13, /* represents a list of links to other objects in external models */


		//String types
		String: 20,
		LocalizableString: 21,
		DateTime: 22,    /* ISO 8601 date */
		GeoLocation: 23,    /* LatLonHeight - ISO6709 Annex H string, e.g: "+27.5916+086.5640+8850/" for Mount Everest */
		Position: 24,     /* "x y z w" space separated string representing vector with 2,3 or 4 elements*/

		Url: 25, /* URL string */

		//List type
		StringList: 40, /* Tag */

		//TODO: Do we need explicit logical types for any others?
	};

//Bitmask values for boolean attribute options
export const AttributeFlags =
	{
		afHidden: 1 << 0, /* Attribute will not be displayed in default GUI property views. */
		afDontIndex: 1 << 1, /* NOT USED Attribute will not be indexed by the search service. */
		afDtHashV2: 1 << 1, /* in DT storage, used for DtParams that use content based hash function rather than UUID. This use overrides AfDontIndex from Forge, which we don't use */
		afDirectStorage: 1 << 2,  /* NOT USED Attribute is not worth de-duplicating (e.g. vertex data or dbId reference) */
		afStream: 1 << 2, /* Actual attribute value on the element would be a stream of values, rather than an individual value */
		afReadOnly: 1 << 3, /* Attribute is read-only (used when writing back to the design model, in e.g. Revit) */
		afDtParam: 1 << 4, /* Attribute is a native DT parameter */

		//Added post-Tandem to Forge here: https://git.autodesk.com/A360/platform-translation-propertydb/blob/master/propertydb/PropertyDatabase.h#L100
		afStable: 1 << 5, /* NOT USED Attribute value should be stable between file versions (e.g. Revit Element ID, AutoCAD Entity Handle) */

		afStandard: 1 << 6, /* Attribute is a standard attribute. This is not synchronized with the server and is runtime only */
	};

//Indicates what objects the attribute applies to. Can be a combination, for example, a setting of "et"
//means that the attribute applies to both elements and types
export const AttributeContext = {
	Element: "e",
	Type: "t",
	Space: "s",
	Facility: "f",
	DONOTUSE_Logical: "l",
};

export function isAttributeTypeNumeric(type) {
	return (
		type === AttributeType.Integer ||
		type === AttributeType.Double ||
		type === AttributeType.Float
	);
}

export function isAttributeTypeString(type) {
	return Number(type) === AttributeType.String;
}

export function isAttributeTypeDateTime(type) {
	return Number(type) === AttributeType.DateTime;
}

/**
 * Finds the index of the bucket that contains the given value.
 * @param {Array<{lower?: number, upper?: number}>} buckets
 * @param {number} value
 * @returns {number} - The index of the bucket, -1 if not found.
 */
export function findBucketIdxByValue(buckets, value) {
	if (!Array.isArray(buckets) || value == null) {
		return -1;
	}

	// TODO: buckets are sorted, so we could use binary search here for performance
	for (let i = 0; i < buckets.length; i++) {
		const { lower, upper } = buckets[i];
		if ((lower === undefined || value >= lower) && (upper === undefined || value < upper)) {
			return i;
		}
	}
	return -1;
}

/**
 * Creates a bucket object from val and bucket definition.
 * @return {Bucket} - { id, lower, upper, label, sortOrder }
 */
export function createBucket(val, bucketDef, attr, formatter) {
	const { span, rolling } = bucketDef;
	const type = attr.dataType;
	const symbol = attr.forgeSymbol;
	const duration = span === 'W' ? 'week' : span === 'M' ? 'month' : 'year';

	/**
	 * Format date label
	 * @param {Date} date - The date to format.
	 * @param {number} [count] - If defined then it is a rolling bucket count.
	 * */
	const getDateLabel = (date, count) => {
		// rolling bucket labels
		if (count !== undefined) {
			if (count === 0 && date > new Date()) {
				return `next ${duration}`;
			} else if (count === 0) {
				return `past ${duration}`;
			} else if (count < 0) {
				return `${Math.abs(count)} ${duration}${count < -1 ? 's' : ''} ago`;
			}
			return `in ${count} ${duration}${count > 1 ? 's' : ''}`;
		}

		// non-rolling bucket labels
		if (span === 'W') {
			const d = dayjs(date);
			const weekNo = d.isoWeek();
			const year = d.isoWeekYear ? d.isoWeekYear() : d.year();
			return `W${weekNo}/${year}`;
		} else if (span === 'M') {
			return `${date.getMonth() + 1}/${date.getFullYear()}`;
		}
		return `${date.getFullYear()}`;
	};

	/** Get interval from date val */
	const getInterval = (date) => {
		const d = dayjs(date);
		if (rolling) {
			const now = dayjs();
			let start, end;
			let count = 0;

			// Move interval window until date falls within [start, end)
			if (date > now.toDate()) {
				end = now.clone().add(1, duration)
				while (date >= end.toDate()) {
					end = end.add(1, duration);
					count++;
				}
				start = end.clone().subtract(1, duration);
			} else {
				start = now.clone().subtract(1, duration);
				while (date < start.toDate()) {
					start = start.subtract(1, duration);
					count--;
				}
				end = start.clone().add(1, duration);
			}

			return {
				lower: start.toDate(),
				upper: end.toDate(),
				count
			};
		}
		return {
			lower: d.startOf(duration).toDate(),
			upper: d.endOf(duration).toDate()
		};
	};

	/** When storing the bucket boundaries match the value date format */
	const formatBoundary = (date) => {
		switch (symbol) {
			case 'date':
				return dayjs(date).format('YYYY-MM-DD');
			case 'dateAndTime':
				return dayjs(date).format('YYYY-MM-DDTHH:mm:ss');
			default: // isoDate
				return date.toISOString();
		}
	};

	if (type === AttributeType.DateTime) {
		const valDate = new Date(val);
		let { lower, upper, count } = getInterval(valDate);
		return {
			id: getDateLabel(valDate, count),
			lower: formatBoundary(lower),
			upper: formatBoundary(upper),
			label: getDateLabel(valDate, count),
			sortOrder: valDate.getTime(),
		};
	} else if (type === AttributeType.Integer || type === AttributeType.Double || type === AttributeType.Float) {
		const lower = Math.floor(val / span) * span; // find nearest bucket lower bound
		const upper = lower + span;

		return {
			id: `${lower}-${upper}`,
			lower,
			upper,
			label: `${Number.parseFloat(formatter(lower))} - ${formatter(upper)}`,
			sortOrder: lower,
		};
	}
}

/** Compares two bucket definitions for equality */
export function isEqualBucketDefs(a, b) {
	if (!a && !b) return true;
	if (!a || !b) return false;
	return a.span === b.span && a.rolling === b.rolling;
}

// Takes an input and parses it according to specified data type
// This is a useful helper when you need to apply some mutations to the property db from UI (data grid editing, bulk excel import, etc)
export function parseInputAttrValue(value, type, useDefault = true) {
	switch (type) {
		case AttributeType.Integer: {
			// empty
			if (value === undefined || value === null || value === '') {
				return undefined;
			}
			const parsed = Number.parseInt(value);
			if (isNaN(parsed)) {
				return useDefault ? 0 : undefined;
			}
			return parsed;
		}
		case AttributeType.Double: {
			// empty
			if (value === undefined || value === null || value === '') {
				return undefined;
			}
			const parsed = Number(value);
			if (isNaN(parsed)) {
				return useDefault ? 0 : undefined;
			}
			return parsed;
		}
		case AttributeType.Float: {
			// empty
			if (value === undefined || value === null || value === '') {
				return undefined;
			}
			const parsed = Number.parseFloat(value);
			if (isNaN(parsed)) {
				return useDefault ? 0 : undefined;
			}
			return parsed;
		}
		case AttributeType.String: {
			// empty
			if (value === undefined || value === null) {
				return undefined;
			}
			return value.toString();
		}
		case AttributeType.StringList: {
			// empty
			if (value === undefined || value === null || value === '') {
				return undefined;
			}
			let parsed = value;
			if (typeof parsed === "string") {
				try {
					parsed = JSON.parse(parsed);
				} catch { /* Failure to parse will generate a warning below */ }
			}
			if (!Array.isArray(parsed)) {
				console.warn("Expected an Array parsing StringList value, but got:", `"${parsed}"`);
				parsed = [parsed];
			}

			// return as sorted array
			return parsed.map((v) => v.toString()).sort().filter(Boolean);
		}
		case AttributeType.DateTime: {
			// empty
			if (value === undefined || value === null) {
				return undefined;
			}
			return value || '';
		}
		case AttributeType.Boolean: {
			// empty
			if (value === undefined || value === null || value === '') {
				return undefined;
			}
			return typeof value === "boolean"
				? value ? 1 : 0
				: Number.parseInt(value) ? 1 : 0;
		}
		default:
			return value;
	}
}

//Computed in the same way as stored on the server side.
//See https://git.autodesk.com/tandem/dt-server/blob/master/src/autodesk.com/double-trouble/btstore/attribute.go#L176
export function hashDtAttrV1(attr) {
	return `[${attr.uuid}][${attr.dataType}]`;
}

//IMPORTANT: Logic needs to match MatchesContext in attribute.go in dt-server
export const matchAttributeContext = (dtAttrContext, flag, schemaVersion) => {
	const CTX = AttributeContext;
	const EF = ElementFlags;
	let result = false;

	if (!dtAttrContext) {
		return false;
	}

	//Loop over each character in the context string, which correspond to a specific context and check
	//whether the current element is matching that context
	for (const c of dtAttrContext) {
		//Spaces are not always classified, so we have to
		//match them via checking the attribute context. Same applies to Facility parameters.
		//For every other element, the parameter is either stored in its Type element or
		//directly in the element itself.
		switch (c) {
			case CTX.Space:
				result = result || (flag === EF.Room || flag === EF.Level || flag === EF.Location);
				break;
			case CTX.Type:
				result = result || isFamilyType(schemaVersion, flag);
				break;
			case CTX.Facility:
			//Use Element context for DocumentRoot
			case CTX.DONOTUSE_Logical:
			//Streams, Levels, and Generic Assets are logical elements and the element context applies
			case CTX.Element:
				result = result || ((flag & EF.AllLogicalMask) === 0 || flag === EF.Level || flag === streamFlag(schemaVersion) || flag === EF.GenericAsset || flag === EF.System || flag === EF.Ticket || flag === EF.DocumentRoot);
				break;
			default:
				break;
		}
	}

	return result;
};

export class AttributeDef {

	constructor(attrId, attrSchema) {

		this.id = attrId;

		//attrName(0), category(1), dataType(2), dataTypeContext(3), description(4), displayName(5), flags(6), precision(7), forgeUnit(8)
		this.name = attrSchema[0];
		this.category = attrSchema[1];
		this.dataType = attrSchema[2];
		this.dataTypeContext = attrSchema[3];
		this.description = attrSchema[4];
		// similar to `formatProperty` a built-in fallback so usage can rely on `displayName`.
		this.displayName = attrSchema[5] || this.name;
		this.flags = attrSchema[6];
		this.precision = attrSchema[7];
		this.forgeUnit = attrSchema[8];

		//DT native atribute?
		if (attrSchema.length > 9) {
			this.forgeSymbol = attrSchema[9];
			this.forgeSpec = attrSchema[10];
			this.uuid = attrSchema[11];
			this.groupUuid = attrSchema[12];
			this.applicationFilter = attrSchema[13];
			this.allowedValues = attrSchema[14];
			this.context = attrSchema[15] || AttributeContext.Element;
			this.displayOptions = attrSchema[16];
		}

		//Computed in the same way as stored on the server side.
		//See https://git.autodesk.com/tandem/dt-server/blob/master/src/autodesk.com/double-trouble/btstore/attribute.go#L176
		//Generally the computation needs to be kept in sync for Excel import to work, not for talking to the server APIs, where
		//the attribute ID is used exclusively
		if (this.isNative()) {
			if (this.useHashV2()) {
				this.hash = `${ColumnFamilies.DtProperties}[${this.category}][${this.name}][${this.dataType}]`;
			} else {
				this.hash = hashDtAttrV1(this);
			}
		} else {
			this.hash = `[${this.category}][${this.name}][${this.forgeUnit || ""}][${this.forgeUnit ? "" : (this.dataTypeContext || "")}]`;
		}

		this.readOnly = Boolean(this.flags & AttributeFlags.afReadOnly);
		this.guessDataFormatType();

		if (this.dataType === AttributeType.String && this.flags & AttributeFlags.afStream) {
			// May be absent if a previously streamable attribute's allowed values are removed from the template
			this.allowedValues.list = [];

			// Connected enum attribute, convert allowed list to reverse lookup (index -> enum)
			for (const strKey in this.allowedValues.map) {
				const value = this.allowedValues.map[strKey];
				const index = value.index ?? value;
				if (index > 0) this.allowedValues.list[index] = strKey;
			}
		}

		if (this.displayOptions) {
			// Convert display options to a map for easier access
			const displayOptionsMap = {};
			for (const option of this.displayOptions) {
				// Keyed by label
				displayOptionsMap[option.l] = option;
			}
			this.displayOptions = displayOptionsMap;
		}
	}

	isNative() {
		return (this.flags & AttributeFlags.afDtParam) !== 0;
	}

	useHashV2() {
		return (this.flags & AttributeFlags.afDtHashV2) !== 0;
	}

	isHidden() {
		return (this.flags & AttributeFlags.afHidden) !== 0;
	}

	isStandard() {
		return (this.flags & AttributeFlags.afStandard) !== 0;
	}

	guessDataFormatType() {
		let attr = this;
		switch (attr.dataType) {
			case AttributeType.Boolean:
				attr.type = "boolean";
				break;
			case AttributeType.Integer:
				attr.type = "integer";
				break;
			case AttributeType.Float:
			case AttributeType.Double:
				attr.type = "number";
				break;
		}

		if (attr.forgeUnit === "feet") {
			//TODO: The formatting for each Revit parameter is well known by Revit, but we have no way
			//to get it for pre-2021 Revit data. The below are just some examples to make a large subset of parameters look reasonable
			//E.g. look at what people do here: https://spiderinnet.typepad.com/blog/2014/08/revit-units-net-api-figure-out-revit-internal-units-per-unit-type.html
			const fr_inch = ["Default Thickness", "Thickness", "Actual Tread Depth", "Actual Riser Height", "Tread Thickness", "Minimum Tread Depth", "Maximum Riser Height"];
			const fr_feet_inch = ["Height Offset From Level", "Top Offset", "Base Offset", "Width", "Base Offset From Level"];

			//These map the hardcoded Forge Unit types used by Revit 2021 for the same thing
			if (fr_inch.indexOf(attr.name) >= 0) {
				attr.forgeUnit = "fractionalInches";
				attr.scaleFactor = 12; //Need this because fractionalInches will format the value like it's comin in inches, while here we map incoming "foot" unit to inches.
			} else if (fr_feet_inch.indexOf(attr.name) >= 0) {
				attr.forgeUnit = "feetFractionalInches";
			}
		}
	}

	formatAttrValue(val) {
		let attr = this;
		if (val === undefined || val === null) {
			return val;
		}
		switch (attr.dataType) {
			case AttributeType.Integer:
				if (typeof val !== "number") {
					console.warn("Expected integer, but got", val);
					return parseInt(val);
				}
				break;
			case AttributeType.Float:
			case AttributeType.Double:
				if (typeof val !== "number") {
					console.warn("Expected number, but got", val);
					return parseFloat(val);
				} else {
					if (attr.scaleFactor) {
						val *= attr.scaleFactor;
					}

					if (Number.isSafeInteger(val)) {
						return val | 0;
					}
				}
				break;
			case AttributeType.BLOB:
				if (attr.id === QC.Config || attr.id === QC.Settings) {
					// Parse known JSON blobs
					const arr = new Uint8Array(base64DecodedLen(val.length));
					base64DecToArr(val, arr);
					return blobToJson(arr);
				}
		}
		return val;
	}


}
